import { t } from 'i18next';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import AurPopup from '../common/aur-popup/AurPopup';

export interface IdleManagerPopupProps {
  isOpen: boolean;
  initialCountdownSec: number;
  onStayLoggedIn: () => void;
  onLogOut: () => void;
}

/**
 * Pure presentational component for the idle/session timeout warning modal.
 * Manages its own countdown display internally. All session management logic
 * (idle detection, activity tracking) lives in PrivateRoute.
 */
const IdleManagerPopup: FC<IdleManagerPopupProps> = ({
  isOpen,
  initialCountdownSec,
  onStayLoggedIn,
  onLogOut,
}) => {
  const [countdownTime, setCountdownTime] = useState(initialCountdownSec);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const clearCountdown = useCallback(() => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  // Start countdown when popup opens, clear it when popup closes
  useEffect(() => {
    if (!isOpen) {
      clearCountdown();
      setCountdownTime(initialCountdownSec);
      return;
    }

    const startTime = Date.now();
    setCountdownTime(initialCountdownSec);

    countdownIntervalRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      const remaining = Math.max(0, initialCountdownSec - elapsed);
      setCountdownTime(remaining);

      if (remaining <= 0) {
        clearCountdown();
        onLogOut();
      }
    }, 1000);

    return clearCountdown;
  }, [isOpen, onLogOut, initialCountdownSec, clearCountdown]);

  const formatTime = useCallback((timeInSeconds: number): string => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = timeInSeconds % 60;
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  }, []);

  if (!isOpen) return null;

  return (
    <AurPopup
      className="inactivity-popup-modal"
      open={isOpen}
      onOk={onStayLoggedIn}
      onCancel={onLogOut}
      showPopupHeading={true}
      popupHeading={t('common.sessionTimeoutModule.message')}
      popupTimer={formatTime(countdownTime)}
      popupContent={t('common.sessionTimeoutModule.extendSessionMessage')}
      labelActionCancel={t('common.sessionTimeoutModule.logOut')}
      labelActionOk={t('common.sessionTimeoutModule.continueBtn')}
      cancelButtonTitle=""
    />
  );
};

export default React.memo(IdleManagerPopup);
